import React, { useState, useEffect } from 'react';
import { Tab } from 'semantic-ui-react';
import { defineMessages, useIntl } from 'react-intl';
import { TextWidget } from '@plone/volto/components';
import RichTextRenderer from '../Views/TeaserCard/RichTextRenderer';
import showdown from 'showdown';

const messages = defineMessages({
  edit: {
    id: 'Edit',
    defaultMessage: 'Edit',
  },
  preview: {
    id: 'Preview',
    defaultMessage: 'Preview',
  },
  htmlCode: {
    id: 'HTML Code',
    defaultMessage: 'HTML Code',
  },
  markdown: {
    id: 'Markdown',
    defaultMessage: 'Markdown',
  },
});

/**
 * Widget personalizado para editar contenido HTML con vista previa
 */
const EnhancedRichTextWidget = (props) => {
  const intl = useIntl();
  const [activeIndex, setActiveIndex] = useState(0);
  const [htmlValue, setHtmlValue] = useState(props.value?.data || '');
  const [markdownValue, setMarkdownValue] = useState('');

  // Inicializar el conversor de Markdown
  const converter = new showdown.Converter({
    tables: true,
    simplifiedAutoLink: true,
    strikethrough: true,
    tasklists: true,
  });

  // Efecto para convertir HTML a Markdown al cargar el componente
  useEffect(() => {
    if (htmlValue) {
      try {
        // Showdown no tiene un convertidor directo de HTML a Markdown
        // Por lo que mantenemos el HTML como fuente principal
        setMarkdownValue(htmlToMarkdown(htmlValue));
      } catch (error) {
        console.error('Error al convertir HTML a Markdown:', error);
      }
    }
  }, []);

  // Función mejorada para convertir HTML a Markdown
  const htmlToMarkdown = (html) => {
    if (!html) return '';

    try {
      // Crear un elemento temporal para manejar el HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;

      // Función recursiva para procesar nodos
      const processNode = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          return node.textContent;
        }

        if (node.nodeType !== Node.ELEMENT_NODE) {
          return '';
        }

        let result = '';
        const tagName = node.tagName.toLowerCase();

        switch (tagName) {
          case 'p':
            result = processChildren(node) + '\n\n';
            break;
          case 'h1':
            result = '# ' + processChildren(node) + '\n\n';
            break;
          case 'h2':
            result = '## ' + processChildren(node) + '\n\n';
            break;
          case 'h3':
            result = '### ' + processChildren(node) + '\n\n';
            break;
          case 'h4':
            result = '#### ' + processChildren(node) + '\n\n';
            break;
          case 'h5':
            result = '##### ' + processChildren(node) + '\n\n';
            break;
          case 'h6':
            result = '###### ' + processChildren(node) + '\n\n';
            break;
          case 'strong':
          case 'b':
            result = '**' + processChildren(node) + '**';
            break;
          case 'em':
          case 'i':
            result = '*' + processChildren(node) + '*';
            break;
          case 'a':
            const href = node.getAttribute('href') || '';
            result = '[' + processChildren(node) + '](' + href + ')';
            break;
          case 'img':
            const src = node.getAttribute('src') || '';
            const alt = node.getAttribute('alt') || '';
            result = '![' + alt + '](' + src + ')';
            break;
          case 'ul':
            result = processListItems(node, '- ') + '\n';
            break;
          case 'ol':
            result = processListItems(node, null, true) + '\n';
            break;
          case 'li':
            // Los elementos li se manejan en processListItems
            result = processChildren(node);
            break;
          case 'blockquote':
            // Procesar cada línea y añadir '> ' al principio
            result =
              '> ' + processChildren(node).split('\n').join('\n> ') + '\n\n';
            break;
          case 'pre':
          case 'code':
            result = '```\n' + processChildren(node) + '\n```\n\n';
            break;
          case 'hr':
            result = '---\n\n';
            break;
          case 'br':
            result = '\n';
            break;
          case 'div':
            // Para divs que contienen iframes (videos)
            if (node.querySelector('iframe')) {
              const iframe = node.querySelector('iframe');
              if (iframe) {
                const iframeSrc = iframe.getAttribute('src') || '';
                result = `\n\n<iframe src="${iframeSrc}" width="560" height="315" frameborder="0" allowfullscreen></iframe>\n\n`;
              } else {
                result = processChildren(node);
              }
            } else {
              // Para otros divs, procesar su contenido normalmente
              result = processChildren(node);
              // Si el div no está al final de un párrafo, añadir saltos de línea
              if (result && !result.endsWith('\n\n')) {
                result += '\n\n';
              }
            }
            break;
          default:
            // Para otros elementos, simplemente procesar sus hijos
            result = processChildren(node);
        }

        return result;
      };

      // Procesar hijos de un nodo
      const processChildren = (node) => {
        let result = '';
        for (let i = 0; i < node.childNodes.length; i++) {
          result += processNode(node.childNodes[i]);
        }
        return result;
      };

      // Procesar elementos de lista
      const processListItems = (listNode, prefix, ordered = false) => {
        let result = '\n';
        let counter = 1;

        for (let i = 0; i < listNode.children.length; i++) {
          const item = listNode.children[i];
          if (item.tagName.toLowerCase() === 'li') {
            if (ordered) {
              result += `${counter++}. ${processChildren(item)}\n`;
            } else {
              result += `${prefix}${processChildren(item)}\n`;
            }
          }
        }

        return result;
      };

      // Procesar el HTML completo
      let markdown = '';
      for (let i = 0; i < tempDiv.childNodes.length; i++) {
        markdown += processNode(tempDiv.childNodes[i]);
      }

      // Limpiar espacios en blanco excesivos
      markdown = markdown.replace(/\n{3,}/g, '\n\n');

      return markdown.trim();
    } catch (error) {
      console.error('Error al convertir HTML a Markdown:', error);

      // Fallback a la conversión básica si hay errores
      let md = html;
      // Párrafos
      md = md.replace(/<p>(.*?)<\/p>/g, '$1\n\n');
      // Encabezados
      md = md.replace(/<h1>(.*?)<\/h1>/g, '# $1\n\n');
      md = md.replace(/<h2>(.*?)<\/h2>/g, '## $1\n\n');
      md = md.replace(/<h3>(.*?)<\/h3>/g, '### $1\n\n');
      // Negritas
      md = md.replace(/<strong>(.*?)<\/strong>/g, '**$1**');
      // Cursivas
      md = md.replace(/<em>(.*?)<\/em>/g, '*$1*');
      // Enlaces
      md = md.replace(/<a href="(.*?)".*?>(.*?)<\/a>/g, '[$2]($1)');
      // Imágenes
      md = md.replace(/<img src="(.*?)" alt="(.*?)".*?\/>/g, '![$2]($1)');

      return md.trim();
    }
  };

  // Manejador para cambios en el editor HTML
  const handleHtmlChange = (id, value) => {
    setHtmlValue(value);

    // Actualizar también el valor de Markdown cuando cambia el HTML
    try {
      setMarkdownValue(htmlToMarkdown(value));
    } catch (error) {
      console.error('Error al convertir HTML a Markdown:', error);
    }

    props.onChange(id, {
      'content-type': 'text/html',
      data: value,
      encoding: 'utf8',
    });
  };

  // Manejador para cambios en el editor Markdown
  const handleMarkdownChange = (event) => {
    const mdValue = event.target.value;
    setMarkdownValue(mdValue);

    // Convertir Markdown a HTML
    const htmlValue = converter.makeHtml(mdValue);
    setHtmlValue(htmlValue);

    props.onChange(props.id, {
      'content-type': 'text/html',
      data: htmlValue,
      encoding: 'utf8',
    });
  };

  // Función para aplicar formato al texto seleccionado
  const applyFormat = (format, options = {}) => {
    const textarea = document.getElementById(`${props.id}-html-textarea`);
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = htmlValue.substring(start, end);
    let newText = '';
    let newCursorPos = 0;

    switch (format) {
      case 'bold':
        newText = `<strong>${selectedText}</strong>`;
        newCursorPos = start + 8 + selectedText.length;
        break;
      case 'italic':
        newText = `<em>${selectedText}</em>`;
        newCursorPos = start + 4 + selectedText.length;
        break;
      case 'link':
        const url = options.url || 'https://';
        newText = `<a href="${url}" target="_blank">${selectedText || url}</a>`;
        newCursorPos = start + newText.length;
        break;
      case 'image':
        const imgUrl = options.url || '';
        const alt = options.alt || '';
        newText = `<img src="${imgUrl}" alt="${alt}" />`;
        newCursorPos = start + newText.length;
        break;
      case 'video':
        const videoUrl = options.url || '';
        // Asegurarse de que la URL tenga el formato correcto para YouTube
        const formattedVideoUrl = formatVideoUrl(videoUrl);
        newText = `<div class="iframe-wrapper" style="position: relative; width: 100%; padding-top: 56.25%; margin-bottom: 1.5rem;"><iframe src="${formattedVideoUrl}" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;" frameborder="0" allowfullscreen></iframe></div>`;
        newCursorPos = start + newText.length;
        break;
      default:
        return;
    }

    const newValue =
      htmlValue.substring(0, start) + newText + htmlValue.substring(end);
    handleHtmlChange(props.id, newValue);

    // Restaurar el foco y la posición del cursor después de la actualización
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start, newCursorPos);
    }, 0);
  };

  // Función para formatear URLs de video (especialmente YouTube)
  const formatVideoUrl = (url) => {
    if (!url) return '';

    // Asegurarse de que la URL comienza con http:// o https://
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }

    // Convertir URL de YouTube normal a URL de embed
    if (url.includes('youtube.com/watch')) {
      const videoId = url.split('v=')[1]?.split('&')[0];
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}?rel=0&showinfo=0`;
      }
    }

    // Convertir URL corta de YouTube a URL de embed
    if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1]?.split('?')[0];
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}?rel=0&showinfo=0`;
      }
    }

    // Convertir URL de Vimeo a URL de embed
    if (url.includes('vimeo.com/')) {
      const videoId = url.split('vimeo.com/')[1]?.split('?')[0];
      if (videoId) {
        return `https://player.vimeo.com/video/${videoId}`;
      }
    }

    return url;
  };

  // Función para insertar un video
  const insertVideo = () => {
    const url = prompt('Introduce la URL del video (YouTube, Vimeo, etc.):', 'https://');
    if (!url) return;

    const formattedVideoUrl = formatVideoUrl(url);
    
    // Insertar el iframe con la clase wrapper para mantener la relación de aspecto
    const newText = `<div class="iframe-wrapper" style="position: relative; width: 100%; padding-top: 56.25%; margin-bottom: 1.5rem;"><iframe src="${formattedVideoUrl}" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;" frameborder="0" allowfullscreen></iframe></div>`;
    
    // Actualizar el valor HTML
    const newValue = htmlValue + newText;
    handleHtmlChange(props.id, newValue);
    
    // Actualizar el valor Markdown
    const newMarkdownValue = markdownValue + `\n\n<iframe src="${formattedVideoUrl}" width="560" height="315" frameborder="0" allowfullscreen></iframe>\n\n`;
    setMarkdownValue(newMarkdownValue);
  };

  const panes = [
    {
      menuItem: intl.formatMessage(messages.htmlCode),
      render: () => (
        <Tab.Pane>
          <div className="editor-toolbar">
            <button
              type="button"
              className="ui button"
              onClick={(e) => {
                e.preventDefault();
                applyFormat('bold');
              }}
            >
              <i className="bold icon"></i>
            </button>
            <button
              type="button"
              className="ui button"
              onClick={(e) => {
                e.preventDefault();
                applyFormat('italic');
              }}
            >
              <i className="italic icon"></i>
            </button>
            <button
              type="button"
              className="ui button"
              onClick={(e) => {
                e.preventDefault();
                const url = prompt('Introduce la URL del enlace:', 'https://');
                if (url) {
                  applyFormat('link', { url });
                }
              }}
            >
              <i className="linkify icon"></i>
            </button>
            <button
              type="button"
              className="ui button"
              onClick={(e) => {
                e.preventDefault();
                const url = prompt(
                  'Introduce la URL de la imagen:',
                  'https://',
                );
                if (url) {
                  const alt = prompt('Introduce el texto alternativo:', '');
                  applyFormat('image', { url, alt });
                }
              }}
            >
              <i className="image icon"></i>
            </button>
            <button
              type="button"
              className="ui button"
              onClick={(e) => {
                e.preventDefault();
                insertVideo();
              }}
            >
              <i className="video icon"></i>
            </button>
            <button
              type="button"
              className="ui button youtube-btn"
              onClick={(e) => {
                e.preventDefault();
                const url = prompt('Introduce la URL del video de YouTube:', 'https://www.youtube.com/watch?v=');
                if (!url) return;
                
                const formattedVideoUrl = formatVideoUrl(url);
                if (formattedVideoUrl.includes('youtube.com/embed/')) {
                  // Insertar el iframe con la clase wrapper para mantener la relación de aspecto
                  const newText = `<div class="iframe-wrapper" style="position: relative; width: 100%; padding-top: 56.25%; margin-bottom: 1.5rem;"><iframe src="${formattedVideoUrl}" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;" frameborder="0" allowfullscreen></iframe></div>`;
                  
                  // Actualizar el valor HTML
                  const newValue = htmlValue + newText;
                  handleHtmlChange(props.id, newValue);
                  
                  // Actualizar el valor Markdown
                  const newMarkdownValue = markdownValue + `\n\n<iframe src="${formattedVideoUrl}" width="560" height="315" frameborder="0" allowfullscreen></iframe>\n\n`;
                  setMarkdownValue(newMarkdownValue);
                } else {
                  alert('La URL proporcionada no parece ser una URL válida de YouTube.');
                }
              }}
            >
              <i className="youtube icon"></i>
            </button>
          </div>
          <div className="html-textarea-container">
            <textarea
              id={`${props.id}-html-textarea`}
              value={htmlValue}
              onChange={(e) => handleHtmlChange(props.id, e.target.value)}
              rows={20}
              className="html-editor-textarea"
              placeholder="<p>Escribe o pega tu HTML aquí...</p>"
            />
          </div>
        </Tab.Pane>
      ),
    },
    {
      menuItem: intl.formatMessage(messages.markdown),
      render: () => (
        <Tab.Pane>
          <div className="editor-toolbar">
            <button
              type="button"
              className="ui button"
              onClick={(e) => {
                e.preventDefault();
                const textarea = document.getElementById(
                  `${props.id}-markdown-textarea`,
                );
                if (!textarea) return;

                const start = textarea.selectionStart;
                const end = textarea.selectionEnd;
                const selectedText = markdownValue.substring(start, end);
                const newText = `**${selectedText}**`;

                const newValue =
                  markdownValue.substring(0, start) +
                  newText +
                  markdownValue.substring(end);
                const newCursorPos = start + 2 + selectedText.length;

                setMarkdownValue(newValue);
                handleMarkdownChange({ target: { value: newValue } });

                setTimeout(() => {
                  textarea.focus();
                  textarea.setSelectionRange(
                    start + 2,
                    start + 2 + selectedText.length,
                  );
                }, 0);
              }}
            >
              <i className="bold icon"></i>
            </button>
            <button
              type="button"
              className="ui button"
              onClick={(e) => {
                e.preventDefault();
                const textarea = document.getElementById(
                  `${props.id}-markdown-textarea`,
                );
                if (!textarea) return;

                const start = textarea.selectionStart;
                const end = textarea.selectionEnd;
                const selectedText = markdownValue.substring(start, end);
                const newText = `*${selectedText}*`;

                const newValue =
                  markdownValue.substring(0, start) +
                  newText +
                  markdownValue.substring(end);
                const newCursorPos = start + 1 + selectedText.length;

                setMarkdownValue(newValue);
                handleMarkdownChange({ target: { value: newValue } });

                setTimeout(() => {
                  textarea.focus();
                  textarea.setSelectionRange(
                    start + 1,
                    start + 1 + selectedText.length,
                  );
                }, 0);
              }}
            >
              <i className="italic icon"></i>
            </button>
            <button
              type="button"
              className="ui button"
              onClick={(e) => {
                e.preventDefault();
                const url = prompt('Introduce la URL del enlace:', 'https://');
                if (!url) return;

                const textarea = document.getElementById(
                  `${props.id}-markdown-textarea`,
                );
                if (!textarea) return;

                const start = textarea.selectionStart;
                const end = textarea.selectionEnd;
                const selectedText = markdownValue.substring(start, end);
                const linkText = selectedText || 'enlace';
                const newText = `[${linkText}](${url})`;

                const newValue =
                  markdownValue.substring(0, start) +
                  newText +
                  markdownValue.substring(end);

                setMarkdownValue(newValue);
                handleMarkdownChange({ target: { value: newValue } });

                setTimeout(() => {
                  textarea.focus();
                  textarea.setSelectionRange(start, start + newText.length);
                }, 0);
              }}
            >
              <i className="linkify icon"></i>
            </button>
            <button
              type="button"
              className="ui button"
              onClick={(e) => {
                e.preventDefault();
                const url = prompt(
                  'Introduce la URL de la imagen:',
                  'https://',
                );
                if (!url) return;

                const alt = prompt('Introduce el texto alternativo:', '');

                const textarea = document.getElementById(
                  `${props.id}-markdown-textarea`,
                );
                if (!textarea) return;

                const start = textarea.selectionStart;
                const newText = `![${alt}](${url})`;

                const newValue =
                  markdownValue.substring(0, start) +
                  newText +
                  markdownValue.substring(start);

                setMarkdownValue(newValue);
                handleMarkdownChange({ target: { value: newValue } });

                setTimeout(() => {
                  textarea.focus();
                  textarea.setSelectionRange(start, start + newText.length);
                }, 0);
              }}
            >
              <i className="image icon"></i>
            </button>
            <button
              type="button"
              className="ui button"
              onClick={(e) => {
                e.preventDefault();
                const url = prompt(
                  'Introduce la URL del video (YouTube, Vimeo, etc.):',
                  'https://',
                );
                if (!url) return;

                const textarea = document.getElementById(
                  `${props.id}-markdown-textarea`,
                );
                if (!textarea) return;

                const start = textarea.selectionStart;
                // Formatear la URL del video correctamente
                const formattedVideoUrl = formatVideoUrl(url);
                // En Markdown no hay una sintaxis estándar para videos, usamos HTML directamente
                const newText = `\n<iframe src="${formattedVideoUrl}" width="560" height="315" frameborder="0" allowfullscreen></iframe>\n`;

                const newValue =
                  markdownValue.substring(0, start) +
                  newText +
                  markdownValue.substring(start);

                setMarkdownValue(newValue);
                handleMarkdownChange({ target: { value: newValue } });

                setTimeout(() => {
                  textarea.focus();
                  textarea.setSelectionRange(start, start + newText.length);
                }, 0);
              }}
            >
              <i className="video icon"></i>
            </button>
            <button
              type="button"
              className="ui button"
              onClick={(e) => {
                e.preventDefault();
                const textarea = document.getElementById(
                  `${props.id}-markdown-textarea`,
                );
                if (!textarea) return;

                const start = textarea.selectionStart;
                const end = textarea.selectionEnd;
                const selectedText = markdownValue.substring(start, end);
                const lines = selectedText ? selectedText.split('\n') : [''];
                const newText = lines.map((line) => `- ${line}`).join('\n');

                const newValue =
                  markdownValue.substring(0, start) +
                  newText +
                  markdownValue.substring(end);

                setMarkdownValue(newValue);
                handleMarkdownChange({ target: { value: newValue } });

                setTimeout(() => {
                  textarea.focus();
                  textarea.setSelectionRange(start, start + newText.length);
                }, 0);
              }}
            >
              <i className="list ul icon"></i>
            </button>
            <button
              type="button"
              className="ui button"
              onClick={(e) => {
                e.preventDefault();
                const textarea = document.getElementById(
                  `${props.id}-markdown-textarea`,
                );
                if (!textarea) return;

                const start = textarea.selectionStart;
                const end = textarea.selectionEnd;
                const selectedText = markdownValue.substring(start, end);
                const lines = selectedText ? selectedText.split('\n') : [''];
                const newText = lines
                  .map((line, index) => `${index + 1}. ${line}`)
                  .join('\n');

                const newValue =
                  markdownValue.substring(0, start) +
                  newText +
                  markdownValue.substring(end);

                setMarkdownValue(newValue);
                handleMarkdownChange({ target: { value: newValue } });

                setTimeout(() => {
                  textarea.focus();
                  textarea.setSelectionRange(start, start + newText.length);
                }, 0);
              }}
            >
              <i className="list ol icon"></i>
            </button>
          </div>
          <div className="markdown-info-message">
            <p className="help-text">
              <i className="info circle icon"></i>
              Puedes usar sintaxis Markdown para dar formato al texto. Usa los
              botones de arriba o escribe directamente usando la sintaxis
              Markdown.
            </p>
          </div>
          <div className="markdown-textarea-container">
            <textarea
              id={`${props.id}-markdown-textarea`}
              value={markdownValue}
              onChange={handleMarkdownChange}
              rows={20}
              className="markdown-editor-textarea"
              placeholder="# Título\n\nEscribe tu **markdown** aquí..."
            />
          </div>
        </Tab.Pane>
      ),
    },
    {
      menuItem: intl.formatMessage(messages.preview),
      render: () => (
        <Tab.Pane>
          <div className="html-preview">
            <RichTextRenderer
              content={htmlValue}
              className="rich-text-content"
            />
          </div>
        </Tab.Pane>
      ),
    },
  ];

  return (
    <div className="enhanced-richtext-widget">
      <Tab
        panes={panes}
        activeIndex={activeIndex}
        onTabChange={(e, { activeIndex }) => setActiveIndex(activeIndex)}
      />
    </div>
  );
};

export default EnhancedRichTextWidget;
